import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";

import axios from "../../utils/axios";
import utils from "../../utils/utils";
import { dispatch } from "../index";

const initialState = {
  exercises: [],
  exercisesObject: {},
  // Lightweight exercise list loaded up front from /uni/get-all-exercises-small.
  // Each item carries denormalized search arrays (bodyPartNames, tagNames, ...)
  // and an imageUrl, mirroring the mobile app's `small_exercises`.
  smallExercises: [],
  smallExercisesObject: {},
  error: null,
  totalPages: 1,
  loading: false,
  limit: 10,
  currentPage: 1,
};

export const fetchExercises = createAsyncThunk(
  "fetchExercises",
  async (_, { dispatch, rejectWithValue }) => {
    let page = 0;
    let shouldContinue = true;

    try {
      while (shouldContinue) {
        const limit = 100;
        const res = await axios.get(`/exercises?size=${limit}&page=${page}`);
        const exercises = res.data.data;

        dispatch(slice.actions.addExercises(exercises));

        if (exercises.length < limit) {
          shouldContinue = false;
        } else {
          page++;
        }
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchSmallExercises = createAsyncThunk(
  "fetchSmallExercises",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.get(`/uni/get-all-exercises-small`);
      // Backend returns a List; normalize to an array regardless.
      const data = Array.isArray(res.data) ? res.data : Object.values(res.data || {});
      dispatch(slice.actions.setSmallExercises(data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const slice = createSlice({
  name: "exercises",
  initialState,
  reducers: {
    setSmallExercises(state, action) {
      state.smallExercises = action.payload;
      state.smallExercisesObject = utils.getUniqueObject(action.payload, "id");
    },
    hasError(state, action) {
      state.error = action.payload;
      state.loading = false;
    },

    startLoading(state, action) {
      state.loading = true;
    },
    stopLoading(state, action) {
      state.loading = false;
    },
    deleteSuccess(state, action) {
      state.loading = false;
      state.exercises = state.exercises.filter(
        (item) => item.id !== action.payload.id
      );
    },
    setCurrentPage(state, action) {
      state.currentPage = action.payload;
    },
    addExercises(state, action) {
      if (state.exercises.length === 0) {
        state.loading = true;
      } else {
        state.loading = false;
      }
      state.exercises.push(...action.payload);
      state.exercises = utils.sortAlphatically(
        state.exercises,
        "exercise_title"
      );
      state.exercisesObject = utils.getUniqueObject(state.exercises, "id");
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchExercises.fulfilled, (state, action) => {});
  },
});

export default slice.reducer;

export function startLoading() {
  return async () => {
    dispatch(slice.actions.startLoading());
  };
}

export function stopLoading() {
  return async () => {
    dispatch(slice.actions.stopLoading());
  };
}

export function addExercise(data, callback = () => {}, errcallback = () => {}) {
  return async () => {
    try {
      const result = await axios.post(`/exercises`, data);
      dispatch(slice.actions.addSuccess(result.data.data));
      callback();
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      errcallback();
    }
  };
}

export function editExercise(
  id,
  data,
  callback = () => {},
  errCallback = () => {}
) {
  return async () => {
    try {
      const result = await axios.put(`/exercises/${id}`, data);
      dispatch(slice.actions.editSuccess(result?.data?.data));
      callback();
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      errCallback();
    }
  };
}

export function deleteExercise(
  id = "",
  callback = () => {},
  errcallback = () => {}
) {
  return async () => {
    try {
      const result = await axios.delete(`/exercises/${id}`);
      callback();
      dispatch(slice.actions.deleteSuccess(result.data.data));
    } catch (error) {
      errcallback();
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function setCurrentPage(page) {
  return async () => {
    dispatch(slice.actions.setCurrentPage(page));
  };
}

export const fetchFolderExercises = createAsyncThunk(
  "fetchFolderExercises",
  async (folderId, thunkAPI) => {
    try {
      const res = await axios.get(`/exercises/folders/${folderId}`);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const selectExercise = (id) =>
  createSelector(
    (state) => state.exercise.exercisesObject,
    (exercises) => exercises[id]
  );
